import React from 'react';
import { motion } from 'framer-motion';
import { Instagram, Twitter, Linkedin, Youtube, Download } from 'lucide-react';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { OctopusLogo } from './OctopusLogo';

interface FooterProps {
  onNavigate?: (page: 'home' | 'about' | 'privacy' | 'terms' | 'contact' | 'error' | 'faq' | 'support' | 'blog' | 'blogPost') => void;
}

const footerLinks = {
  Company: ['About', 'Blog', 'Careers', 'Press'],
  Resources: ['FAQ', 'Help Center', 'Support', 'Contact Us'],
  Features: ['AI Insights', 'VR Role-Play', 'Wearable Sync', 'Community'],
  Legal: ['Privacy Policy', 'Terms of Service', 'Cookie Policy', 'GDPR']
};

const socialLinks = [
  { icon: Instagram, href: '#', label: 'Instagram' },
  { icon: Twitter, href: '#', label: 'Twitter' },
  { icon: Linkedin, href: '#', label: 'LinkedIn' },
  { icon: Youtube, href: '#', label: 'YouTube' }
];

export const Footer: React.FC<FooterProps> = ({ onNavigate }) => {
  const prefersReducedMotion = useReducedMotion();

  const handleLinkClick = (linkText: string) => {
    // Handle navigation to different pages
    switch (linkText) {
      case 'About':
        onNavigate?.('about');
        break;
      case 'Privacy Policy':
        onNavigate?.('privacy');
        break;
      case 'Terms of Service':
        onNavigate?.('terms');
        break;
      case 'Contact Us':
        onNavigate?.('contact');
        break;
      case 'Support':
        onNavigate?.('support');
        break;
      case 'Help Center':
        onNavigate?.('support');
        break;
      case 'FAQ':
        onNavigate?.('faq');
        break;
      case 'Blog':
        onNavigate?.('blog');
        break;
      default:
        console.log(`Navigate to ${linkText}`);
    }
  };

  const handleDownloadApp = () => {
    const downloadSection = document.getElementById('download');
    if (downloadSection) {
      downloadSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      console.log('Download app clicked - would redirect to app stores');
    }
  };

  const handleReduceMotion = () => {
    // This would toggle reduced motion preference
    console.log('Toggle reduced motion preference');
  };

  return (
    <footer className="bg-gray-900 dark:bg-black text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand */}
          <motion.div
            className="lg:col-span-2"
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.button
              onClick={() => onNavigate?.('home')}
              className="flex items-center space-x-3 mb-6 hover:opacity-80 transition-opacity duration-200"
              whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
            >
              <OctopusLogo size="lg" showGlow={true} />
              <span className="text-2xl font-bold">Octify</span>
            </motion.button>
            <p className="text-gray-400 mb-6 max-w-md">
              Transform your relationships with AI-powered insights. Connect better, grow together, and build meaningful connections that last.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-orange-500 transition-colors duration-200"
                  whileHover={prefersReducedMotion ? {} : { scale: 1.1 }}
                  whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Links */}
          {Object.entries(footerLinks).map(([category, links], index) => (
            <motion.div
              key={category}
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="font-semibold mb-4">{category}</h3>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link}>
                    <motion.button
                      onClick={() => handleLinkClick(link)}
                      className="text-gray-400 hover:text-white transition-colors duration-200 text-left"
                      whileHover={prefersReducedMotion ? {} : { x: 2 }}
                    >
                      {link}
                    </motion.button>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Additional Info Section */}
        <motion.div
          className="mt-12 pt-8 border-t border-gray-800"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* Quick Access */}
            <div className="text-center md:text-left">
              <h4 className="font-semibold text-white mb-3">Quick Access</h4>
              <div className="space-y-2">
                <motion.button
                  onClick={() => onNavigate?.('contact')}
                  className="block text-gray-400 hover:text-orange-400 transition-colors duration-200"
                  whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                >
                  Get Help
                </motion.button>
                <motion.button
                  onClick={() => onNavigate?.('privacy')}
                  className="block text-gray-400 hover:text-orange-400 transition-colors duration-200"
                  whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                >
                  Privacy Center
                </motion.button>
              </div>
            </div>

            {/* App Downloads */}
            <div className="text-center">
              <h4 className="font-semibold text-white mb-3">Download App</h4>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <motion.button
                  onClick={handleDownloadApp}
                  className="px-4 py-2 bg-gray-800 rounded-lg text-sm hover:bg-gray-700 transition-colors duration-200 flex items-center gap-2 justify-center"
                  whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                >
                  <Download className="w-4 h-4" />
                  App Store
                </motion.button>
                <motion.button
                  onClick={handleDownloadApp}
                  className="px-4 py-2 bg-gray-800 rounded-lg text-sm hover:bg-gray-700 transition-colors duration-200 flex items-center gap-2 justify-center"
                  whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                >
                  <Download className="w-4 h-4" />
                  Google Play
                </motion.button>
              </div>
            </div>

            {/* Emergency Support */}
            <div className="text-center md:text-right">
              <h4 className="font-semibold text-white mb-3">Need Help?</h4>
              <div className="space-y-2">
                <p className="text-gray-400 text-sm">24/7 Support Available</p>
                <motion.button
                  onClick={() => onNavigate?.('contact')}
                  className="text-orange-400 hover:text-orange-300 transition-colors duration-200 text-sm"
                  whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                >
                  urgent@octify.app
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div
          className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-col md:flex-row items-center gap-4 mb-4 md:mb-0">
            <p className="text-gray-400 text-sm">
              © 2025 Octify. All rights reserved.
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <motion.button
              onClick={() => onNavigate?.('privacy')}
              className="text-gray-400 hover:text-white text-sm transition-colors duration-200"
              whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
            >
              Privacy
            </motion.button>
            <span className="text-gray-600">•</span>
            <motion.button
              onClick={() => onNavigate?.('terms')}
              className="text-gray-400 hover:text-white text-sm transition-colors duration-200"
              whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
            >
              Terms
            </motion.button>
            <span className="text-gray-600">•</span>
            <motion.button
              onClick={handleReduceMotion}
              className="text-gray-400 hover:text-white text-sm transition-colors duration-200"
              whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
            >
              Reduce Motion
            </motion.button>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};